import { Tab } from "@headlessui/react";
import { useMedia } from "shared/providers";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Select, Show } from "shared/components";
import { cx } from "shared/utils";

type Tab = string;

interface ITabSelector<T extends Tab> {
  tabs: T[];
  setActiveTab: (value: T) => void;
  selected: T;
  displayMap?: Record<T, ReactNode>;
  variant?: "filled" | "underline";
}

const SCROLL_THRESHOLD = 3;

const useHorizontalScroll = () => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (viewport) {
      const { scrollLeft, scrollWidth, clientWidth } = viewport;
      setScrollState({
        left: scrollLeft > SCROLL_THRESHOLD,
        right: scrollLeft + clientWidth < scrollWidth - SCROLL_THRESHOLD
      });
    }
  }, []);

  const adjustScroll = useCallback(
    (delta: number) => {
      const viewport = viewportRef.current;
      if (viewport) {
        viewport.scrollLeft += delta;
        updateScrollState();
      }
    },
    [updateScrollState]
  );

  //Scroll the tab bar when holding down the blurred area on the left or right
  const handleScroll = useCallback(
    (direction: "left" | "right") => {
      let isScrolling = true;
      const scrollStep = direction === "left" ? -10 : 10;
      const scroll = () => {
        if (isScrolling) {
          adjustScroll(scrollStep);
          requestAnimationFrame(scroll);
        }
      };
      document.addEventListener("mouseup", () => (isScrolling = false), {
        once: true
      });
      scroll();
    },
    [adjustScroll]
  );

  //horizontal scroll without shift key
  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (e.deltaX === 0 && e.deltaY !== 0) {
        e.preventDefault();
        adjustScroll(e.deltaY);
      }
    },
    [adjustScroll]
  );

  //Blur the left or right tab list when scrolling is possible in that direction
  useEffect(() => {
    const viewport = viewportRef.current;
    viewport?.addEventListener("scroll", updateScrollState);
    updateScrollState();
    return () => viewport?.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  return { viewportRef, scrollState, onWheel, handleScroll };
};

export const Tabs = <T extends Tab>({
  setActiveTab,
  tabs,
  selected,
  displayMap,
  variant = "filled"
}: ITabSelector<T>) => {
  const { isMobile } = useMedia();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [tabListClx, tabClx]: [
    string,
    {
      base: string;
      selected: string;
      inactive: string;
    }
  ] = useMemo(() => {
    return variant === "filled"
      ? [
          "flex space-x-1 rounded-lg bg-[#1C1D220A] p-1",
          {
            base: "w-full rounded-md border px-4 py-2 text-xs transition focus:outline-none",
            selected:
              "border-alpha-100 bg-white font-bold text-content-1000 shadow-sm",
            inactive:
              "border-transparent font-medium text-content-300 shadow-transparent"
          }
        ]
      : [
          "border-b border-alpha-100 flex space-x-4",
          {
            base: "px-2 py-2 outline-none",
            selected:
              "border-b-2 border-primary-500 pb-[6px] font-bold text-primary-500",
            inactive: "font-medium text-content-400"
          }
        ];
  }, [variant]);

  const { viewportRef, scrollState, onWheel, handleScroll } =
    useHorizontalScroll();

  const handleTabClick = useCallback(
    (tab: T, index: number) => {
      setActiveTab(tab);
      const tabElement = tabRefs.current[index];
      if (tabElement)
        tabElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
    },
    [setActiveTab]
  );

  return (
    <>
      <Show if={variant === "filled"}>
        <Select
          className={cx("md:hidden w-full")}
          id={"tab-selector" + variant}
          value={selected}
          onChange={(e) => setActiveTab(e.target.value as T)}
          options={tabs.map((tab) => ({
            value: String(tab),
            label: (
              <p className="flex gap-2">
                {displayMap && displayMap[tab] ? displayMap[tab] : tab}
              </p>
            )
          }))}
        />
      </Show>

      <div className="relative">
        {(["left", "right"] as const).map((direction) =>
          scrollState[direction] ? (
            <div
              key={direction}
              className={cx(
                "absolute inset-y-0 w-8 cursor-pointer",
                direction === "left"
                  ? "left-0 bg-gradient-to-r from-white via-white to-transparent"
                  : "right-0 bg-gradient-to-l from-white via-white to-transparent"
              )}
              onMouseDown={() => handleScroll(direction)}
            />
          ) : null
        )}
        <Tab.Group>
          <Tab.List
            ref={viewportRef}
            onWheel={onWheel}
            className={cx(
              "overflow-x-auto max-w-full hide-scroll",
              tabListClx,
              variant === "filled" ? "hidden md:flex" : "flex"
            )}
          >
            {tabs.map((tab, index) => {
              return (
                <Tab
                  ref={(el) =>
                    (tabRefs.current[index] = el as HTMLButtonElement | null)
                  }
                  onClick={() => handleTabClick(tab, index)}
                  key={tab}
                  className={cx(
                    tabClx.base,
                    selected === tab ? tabClx.selected : tabClx.inactive
                  )}
                >
                  <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                    {isMobile
                      ? displayMap?.[tab] && typeof displayMap[tab] !== "object"
                        ? displayMap[tab]
                        : tab
                      : displayMap?.[tab] || tab}
                  </div>
                </Tab>
              );
            })}
          </Tab.List>
        </Tab.Group>
      </div>
    </>
  );
};
